import { HTTP_METHOD } from '@nxt-insurance/api-service'

import type { StepConfigParams } from '@flows/configs/types'
import type { TextContent } from '@flows/core/i18n/types'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'

const getHttpErrorStatus = (e: unknown): number | undefined => {
  if (typeof e === 'object' && e !== null && 'response' in e) {
    const status = (e as { response?: { status?: number } }).response?.status
    return typeof status === 'number' ? status : undefined
  }
  return undefined
}

export const zipCodeValidatorHookCallback =
  (params: StepConfigParams) => async (flowValues: FlowValues) => {
    try {
      const response = await params.clientHelpers.apiService.call({
        serviceName: 'insurance',
        method: HTTP_METHOD.POST,
        url: `/api/v3/markets/${params.clientHelpers.getMarket()}/products/${
          params.productKey
        }/configuration_params_validations`,
        payload: {
          attributes: {
            zip_code: flowValues.zip_code,
          },
        },
      })
      if ('data' in response && !response.data.success) {
        return {
          error: {
            type: 'translatableText',
            translationKey: 'sharedFlows_common:steps.zip_code.error.notInsurable',
          } as TextContent,
        }
      }
    } catch (error) {
      // Local/staging often returns 404 when this product/route is not deployed; skip check.
      if (getHttpErrorStatus(error) === 404) {
        return undefined
      }
      throw error
    }
  }

export const getZipCodeValidatorHook = (params: StepConfigParams) =>
  ({
    type: 'simpleHook',
    callback: zipCodeValidatorHookCallback(params),
  }) as const satisfies AnySimpleOnSubmitHook

export const getLocationValidatorHook = (params: StepConfigParams) =>
  ({
    type: 'simpleHook',
    callback: async (flowValues: FlowValues) => {
      const zipError = await getZipCodeValidatorHook(params).callback(flowValues)
      if (zipError) {
        return zipError
      }
      const addressResult = await params.clientHelpers.apiService.call<{
        result: {
          address: {
            addressComponents: {
              componentName: { text: string; languageCode: string }
              componentType: string
              confirmationLevel: string
            }[]
          }
        }
      }>({
        serviceName: 'personalizationService',
        method: HTTP_METHOD.POST,
        url: '/v1/address_autocomplete/verify',
        payload: {
          address: {
            regionCode: params.market?.toUpperCase(),
            addressLines: [
              flowValues.street,
              flowValues.houseNumber,
              flowValues.zip_code,
              flowValues.town,
            ],
          },
        },
      })

      if (
        addressResult.data.result.address.addressComponents.some((addressComponent) => {
          return (
            addressComponent.confirmationLevel !== 'CONFIRMED' &&
            addressComponent.confirmationLevel !== 'UNCONFIRMED_BUT_PLAUSIBLE'
          )
        })
      ) {
        return {
          error: {
            type: 'translatableText',
            translationKey: 'sharedFlows_common:steps.invalid_address',
          },
        }
      }
    },
  }) as const satisfies AnySimpleOnSubmitHook
